import styled from "styled-components";
import { FC, useRef } from "react";

import { StyledDialog, CloseButton } from "@/components/AlertDialogs/Dialog";
import Button from "@/components/Button";
import { Modal } from "@/components/Modal";
import { SidePanelFooter } from "@/components/SidePanel";
import Text from "@/components/Text";
import useOnClickOutside from "@/hooks/useOnClickOutside";
import { scrollBarStyles } from "@/utils/theme";

interface Props {
  onClose: () => void;
}

const Dialog = styled(StyledDialog)`
  width: 864px;
  padding: 32px 34px 0 40px;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 32px;
`;

const StyledCloseButton = styled(CloseButton)`
  top: 30px;
  right: 30px;
`;

const GuideItem = styled.div`
  display: flex;
  border-top: 1px solid ${({ theme }) => theme.colors.lightGray};
  &:last-child {
    border-bottom: 1px solid ${({ theme }) => theme.colors.lightGray};
  }
`;

const GuideItemLeft = styled.div`
  width: 64px;
  padding-top: 16px;
  display: flex;
  justify-content: center;
`;

const GuideItemRight = styled.div`
  flex: 1;
  text-align: left;
  padding: 20px 16px 16px;
  border-left: 1px solid ${({ theme }) => theme.colors.lightGray};
`;

const NubmerHolder = styled.div`
  background: ${({ theme }) => theme.colors.purpleLighter};
  width: 32px;
  height: 32px;
  padding-top: 2px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Heading = styled(Text)`
  margin-bottom: 8px;
`;

const StyledSidePanelFooter = styled(SidePanelFooter)`
  margin-left: -40px;
  margin-right: -40px;
  width: calc(100% + 80px);
  button {
    padding: 0 53px;
  }
`;

const GuideItems = styled.div`
  margin-bottom: 16px;
  max-height: calc(73.5vh - 202px);
  overflow-y: auto;
  padding-right: 6px;
  ${scrollBarStyles};
`;

const guideItems = [
  {
    title: "Channel name",
    text: "Select a final channel name, case sensitive. Make sure the name is available.",
  },
  {
    title: "Project",
    text: "Identify the project the new channel is owned by.",
  },
  {
    title: "Manager",
    text: "Identify the person that is manages the channel.",
  },
  {
    title: "Users",
    text: "Identify all other people who would need to access the channel and tasks they would perform.",
  },
  {
    title: "Description",
    text: "Describe the content the channel will have and anything else that you feel is important to know about the channel.",
  },
  {
    title: "Inform",
    text: "Notify all stakeholders about the channels creation.",
  },
];

const ChannelCreationGuide: FC<Props> = ({ onClose }) => {
  const ref = useRef<HTMLDivElement>(null);

  useOnClickOutside(ref, onClose);

  return (
    <Modal zIndex={51}>
      <Dialog
        ref={ref}
        initial={{ scale: 0.5 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.5 }}
      >
        <ModalHeader>
          <Text $type="heading2">Channel creation guide</Text>
          <StyledCloseButton onClick={onClose}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13 1L1 13"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13 13L1 1"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </StyledCloseButton>
        </ModalHeader>
        <GuideItems>
          {guideItems.map((item, index) => (
            <GuideItem key={index}>
              <GuideItemLeft>
                <NubmerHolder>
                  <Text $type="smallBold" $color="white">
                    {index + 1}
                  </Text>
                </NubmerHolder>
              </GuideItemLeft>
              <GuideItemRight>
                <Heading $type="button">{item.title}</Heading>
                <Text $type="smallMedium">{item.text}</Text>
              </GuideItemRight>
            </GuideItem>
          ))}
        </GuideItems>

        <StyledSidePanelFooter>
          <Button $type="secondary" onClick={onClose}>
            Got it
          </Button>
        </StyledSidePanelFooter>
      </Dialog>
    </Modal>
  );
};

export default ChannelCreationGuide;
